/**
 * Studio Works 埋点域（GA4 / Clarity，经 pqTrack）。
 * 基于 pqCreateTrackDomain；业务优先 emit(key, detail)。
 * 旧方法名保留为别名，避免影响已接好的 Hub / 专属页 / 预览。
 */
;(() => {
  if (window.pqStudioTrack) return

  const ENTRY = Object.freeze({
    hub: 'studio_hub',
    groupPage: 'group_page',
    shareLink: 'share_link',
  })

  const ENTRY_VALUES = Object.values(ENTRY)
  const ENTRY_SET = new Set(ENTRY_VALUES)
  const IMPRESSION_RATIO = 0.5
  const IMPRESSION_MS = 1000

  function isStudioEntry(value) {
    return ENTRY_SET.has(String(value || '').trim())
  }

  function entryFromSource(source) {
    const value = String(source || '').trim()
    return isStudioEntry(value) ? value : ''
  }

  function categoryId(value) {
    const raw = value == null ? '' : String(value).trim()
    return raw || 'all'
  }

  function pageSourceFromLocation(search) {
    try {
      const params = new URLSearchParams(search || window.location.search || '')
      const from = (params.get('from') || '').trim()
      if (from === 'studio_hub' || from === 'group_picker') return from
      if (from) return from
    } catch (error) {
      /* ignore */
    }
    return 'direct'
  }

  function withFromParam(url, from) {
    const href = String(url || '').trim()
    const tag = String(from || '').trim()
    if (!href || !tag) return href
    try {
      const base = href.startsWith('http') ? undefined : window.location.origin
      const parsed = new URL(href, base)
      if (!parsed.searchParams.get('from')) parsed.searchParams.set('from', tag)
      if (href.startsWith('/') || href.startsWith('?') || href.startsWith('#')) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`
      }
      return parsed.toString()
    } catch (error) {
      if (href.indexOf('from=') !== -1) return href
      return href + (href.indexOf('?') === -1 ? '?' : '&') + 'from=' + encodeURIComponent(tag)
    }
  }

  function workParams(detail) {
    if (!detail || typeof detail !== 'object') return {}
    const params = {}
    const groupId = detail.group_id || detail.groupId || detail.slug || ''
    const groupName = detail.group_name || detail.groupName || ''
    const itemId = detail.item_id || detail.itemId || detail.uuid || ''
    const itemName = detail.item_name || detail.itemName || detail.title || ''
    const productId = detail.product_id || detail.productId || ''
    const productHandle = detail.product_handle || detail.productHandle || ''
    const entry = entryFromSource(detail.entry_position || detail.entryPosition || detail.source)
    const pageSource = detail.page_source || detail.pageSource || ''
    const hasCategory = detail.category_id != null || detail.categoryId != null
    if (groupId) params.group_id = groupId
    if (groupName) params.group_name = groupName
    if (itemId) params.item_id = itemId
    if (itemName) params.item_name = itemName
    if (productId) params.product_id = productId
    if (productHandle) params.product_handle = productHandle
    if (entry) params.entry_position = entry
    if (pageSource) params.page_source = pageSource
    if (hasCategory) {
      params.category_id = categoryId(
        detail.category_id != null ? detail.category_id : detail.categoryId
      )
    }
    if (detail.success != null && detail.success !== '') {
      params.success = detail.success === true || detail.success === 'true' ? 'true' : 'false'
    }
    if (detail.operation_path) params.operation_path = detail.operation_path
    if (detail.from_group_id) params.from_group_id = detail.from_group_id
    return params
  }

  function observeImpressions(targets, { keyOf, onImpression, root = null } = {}) {
    const nodes = Array.from(targets || []).filter(Boolean)
    if (!nodes.length || typeof onImpression !== 'function') {
      return {
        disconnect() {},
        observe() {},
      }
    }
    const seen = new Set()
    const timers = new Map()

    function stopTimers() {
      timers.forEach((timer) => window.clearTimeout(timer))
      timers.clear()
    }

    if (typeof IntersectionObserver === 'undefined') {
      return {
        disconnect: stopTimers,
        observe() {},
      }
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target
          const key = keyOf ? keyOf(el) : el
          if (!key || seen.has(key)) return
          const visible = entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_RATIO
          const pending = timers.get(el)
          if (visible) {
            if (pending) return
            timers.set(
              el,
              window.setTimeout(() => {
                timers.delete(el)
                if (seen.has(key)) return
                seen.add(key)
                try {
                  onImpression(el, key)
                } catch (error) {
                  /* impression callback must not break scrolling */
                }
                io.unobserve(el)
              }, IMPRESSION_MS)
            )
            return
          }
          if (pending) {
            window.clearTimeout(pending)
            timers.delete(el)
          }
        })
      },
      { root, threshold: [0, IMPRESSION_RATIO, 1] }
    )

    nodes.forEach((node) => io.observe(node))

    return {
      disconnect() {
        stopTimers()
        io.disconnect()
      },
      observe(el) {
        if (!el || !el.isConnected) return
        const key = keyOf ? keyOf(el) : el
        if (!key || seen.has(key)) return
        io.observe(el)
      },
    }
  }

  const createDomain =
    typeof window.pqCreateTrackDomain === 'function'
      ? window.pqCreateTrackDomain
      : function fallbackCreateTrackDomain(options) {
          // 域壳脚本未加载时仍直打 pqTrack，避免 pageView 静默变 no-op
          const entries = (options && options.entries) || {}
          return {
            emit(key, detail) {
              try {
                const def = entries[key]
                if (!def || !def.name || typeof window.pqTrack !== 'function') return false
                const params = Object.assign({}, def.defaults || {}, detail || {})
                return window.pqTrack(def.name, params)
              } catch (error) {
                return false
              }
            },
            entries,
            entryValues: new Set(Array.isArray(options && options.entryValues) ? options.entryValues : []),
          }
        }

  const domain = createDomain({
    prefix: 'studio',
    entryValues: ENTRY_VALUES,
    pick: workParams,
    entries: {
      hub_page_view: { name: 'studio_hub_page_view' },
      group_page_view: { name: 'studio_group_page_view' },
      directory_impression: { name: 'studio_directory_impression' },
      directory_click: { name: 'studio_directory_click' },
      group_page_entry_click: { name: 'studio_group_page_entry_click' },
      group_page_back_click: { name: 'studio_group_page_back_click' },
      group_picker_open: { name: 'studio_group_picker_open' },
      group_switch_click: { name: 'studio_group_switch_click' },
      category_click: { name: 'studio_category_click' },
      work_impression: { name: 'studio_work_impression' },
      preview_open: { name: 'studio_preview_open', needsEntry: true },
      preview_start: {
        name: 'studio_preview_start',
        needsEntry: true,
        defaults: { operation_path: 'start_design' },
      },
      editor_open: {
        name: 'studio_editor_open',
        needsEntry: true,
        defaults: { operation_path: 'start_design' },
      },
      preview_add_to_cart: {
        name: 'studio_preview_add_to_cart',
        needsEntry: true,
        defaults: { operation_path: 'add_to_cart' },
      },
      add_to_cart_result: {
        name: 'studio_add_to_cart_result',
        needsEntry: true,
        defaults: { operation_path: 'add_to_cart' },
      },
      share_landing_miss: { name: 'studio_share_landing_miss' },
    },
  })

  function emit(key, detail) {
    try {
      return domain.emit(key, detail)
    } catch (error) {
      return false
    }
  }

  // 兼容旧调用：方法名 → registry key
  function alias(key, mapDetail) {
    return function aliased(detail) {
      try {
        const payload = typeof mapDetail === 'function' ? mapDetail(detail) : detail
        return emit(key, payload)
      } catch (error) {
        return false
      }
    }
  }

  window.pqStudioTrack = {
    ENTRY,
    isStudioEntry,
    entryFromSource,
    categoryId,
    pageSourceFromLocation,
    withFromParam,
    workParams,
    observeImpressions,
    emit,
    entries: domain.entries,
    // 旧 API 别名（行为不变）
    track(eventName, params) {
      try {
        if (!eventName || typeof window.pqTrack !== 'function') return false
        return window.pqTrack(eventName, params || {})
      } catch (error) {
        return false
      }
    },
    pageView: alias('hub_page_view'),
    groupPageView: alias('group_page_view'),
    directoryImpression: alias('directory_impression'),
    directoryClick: alias('directory_click'),
    groupPageEntryClick: alias('group_page_entry_click'),
    groupPageBackClick: alias('group_page_back_click'),
    groupPickerOpen: alias('group_picker_open'),
    groupSwitchClick: alias('group_switch_click'),
    categoryClick: alias('category_click', (detail) => {
      const next = Object.assign({}, detail || {})
      if (next.category_id != null || next.categoryId != null) {
        next.category_id = categoryId(next.category_id != null ? next.category_id : next.categoryId)
      } else {
        next.category_id = 'all'
      }
      return next
    }),
    workImpression: alias('work_impression'),
    previewOpen: alias('preview_open'),
    previewStart: alias('preview_start'),
    editorOpen: alias('editor_open'),
    previewAddToCart: alias('preview_add_to_cart'),
    addToCartResult: alias('add_to_cart_result'),
    shareLandingMiss: alias('share_landing_miss'),
  }
})()
